import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Row,
  Col,
  Card,
  Space,
  Drawer,
  List,
  Select,
  Descriptions,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { playlistAPI, videoAPI, screenAPI } from '../services/api';
import { themeColors } from '../config/theme';

export default function Playlists() {
  const [playlists, setPlaylists] = useState([]);
  const [videos, setVideos] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, record: null });
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [addVideoForm] = Form.useForm();
  const [addVideoModalOpen, setAddVideoModalOpen] = useState(false);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);

  useEffect(() => {
    fetchPlaylists();
    fetchVideos();
    fetchScreens();
  }, [pagination]);

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ page: 1, limit: 100 });
      setScreens(response.data.data.data);
    } catch (error) {
      console.error('Failed to load screens');
    }
  };

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const response = await playlistAPI.list({
        page: pagination.page,
        limit: pagination.limit,
      });
      setPlaylists(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await videoAPI.list({
        page: 1,
        limit: 100,
        filters: JSON.stringify({ status: 'approved' }),
      });
      setVideos(response.data.data.data);
    } catch (error) {
      console.error('Failed to load videos');
    }
  };

  const fetchPlaylistDetails = async (playlistId) => {
    try {
      setVideosLoading(true);
      const response = await playlistAPI.get(playlistId);
      setPlaylistVideos(response.data.data.items || []);
    } catch (error) {
      message.error('Failed to load playlist details');
    } finally {
      setVideosLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      if (editingId) {
        await playlistAPI.update(editingId, values);
        message.success('Playlist updated successfully');
      } else {
        await playlistAPI.create(values);
        message.success('Playlist created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetchPlaylists();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({ ...record, screenId: record.screen_id });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Playlist',
      content: 'Are you sure you want to delete this playlist?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await playlistAPI.delete(id);
          message.success('Playlist deleted successfully');
          fetchPlaylists();
        } catch (error) {
          message.error('Failed to delete playlist');
        }
      },
    });
  };

  const handleAddVideo = async (values) => {
    try {
      await playlistAPI.addVideo(selectedPlaylistId, values.video_id, playlistVideos.length + 1);
      message.success('Video added to playlist');
      setAddVideoModalOpen(false);
      addVideoForm.resetFields();
      fetchPlaylistDetails(selectedPlaylistId);
    } catch (error) {
      message.error('Failed to add video');
    }
  };

  const handleRemoveVideo = async (videoId) => {
    try {
      await playlistAPI.removeVideo(selectedPlaylistId, videoId);
      message.success('Video removed from playlist');
      fetchPlaylistDetails(selectedPlaylistId);
    } catch (error) {
      message.error('Failed to remove video');
    }
  };

  const handleMoveVideo = async (index, direction) => {
    const newItems = [...playlistVideos];
    if (direction === 'up' && index > 0) {
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
    } else if (direction === 'down' && index < newItems.length - 1) {
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    }

    try {
      const reorderedItems = newItems.map((item, i) => ({
        id: item.id,
        play_order: i + 1,
      }));
      await playlistAPI.reorder(selectedPlaylistId, reorderedItems);
      message.success('Video order updated');
      setPlaylistVideos(newItems);
    } catch (error) {
      message.error('Failed to reorder videos');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Screen',
      dataIndex: 'screen_name',
      key: 'screen_name',
      render: (name) => name || '—',
    },
    {
      title: 'Videos',
      dataIndex: 'video_count',
      key: 'video_count',
      width: 80,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 80,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (
        <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {text || 'N/A'}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setDetailDrawer({ open: true, record });
              fetchPlaylistDetails(record.id);
              setSelectedPlaylistId(record.id);
            }}
          />
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setIsModalOpen(true);
            }}
          >
            Create Playlist
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchPlaylists} loading={loading} />
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={playlists}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => {
              setPagination({ page, limit: pageSize });
            },
          }}
          rowKey="id"
          style={{ color: themeColors.text }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingId ? 'Edit Playlist' : 'Create Playlist'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setEditingId(null);
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="name"
            label="Playlist Name"
            rules={[{ required: true, message: 'Playlist name is required' }]}
          >
            <Input placeholder="e.g., Morning Campaign" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>

          <Form.Item name="screenId" label="Screen">
            <Select
              placeholder="Select a screen (optional)"
              allowClear
              options={screens.map((s) => ({ label: s.screen_name, value: s.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title="Playlist Details"
        placement="right"
        onClose={() => setDetailDrawer({ open: false, record: null })}
        open={detailDrawer.open}
        width={500}
      >
        {detailDrawer.record && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: '20px' }}>
              <Descriptions.Item label="Name">{detailDrawer.record.name}</Descriptions.Item>
              <Descriptions.Item label="Description">
                {detailDrawer.record.description || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Screen">
                {detailDrawer.record.screen_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Version">{detailDrawer.record.version}</Descriptions.Item>
              <Descriptions.Item label="Total Videos">{playlistVideos.length}</Descriptions.Item>
              <Descriptions.Item label="Total Duration">
                {playlistVideos.reduce((sum, v) => sum + (v.duration || 0), 0)} seconds
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(detailDrawer.record.created_at).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: '20px' }}>
              <Button
                type="primary"
                onClick={() => setAddVideoModalOpen(true)}
                block
              >
                Add Video to Playlist
              </Button>
            </div>

            <h4 style={{ color: themeColors.text }}>Videos in Playlist</h4>
            {videosLoading ? (
              <Spin />
            ) : playlistVideos.length === 0 ? (
              <Empty description="No videos in playlist" />
            ) : (
              <List
                dataSource={playlistVideos}
                renderItem={(item, index) => (
                  <List.Item
                    style={{
                      backgroundColor: themeColors.bg,
                      marginBottom: '8px',
                      padding: '12px',
                      borderRadius: '4px',
                    }}
                    actions={[
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={() => handleMoveVideo(index, 'up')}
                      />,
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === playlistVideos.length - 1}
                        onClick={() => handleMoveVideo(index, 'down')}
                      />,
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => handleRemoveVideo(item.video_id)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={`${index + 1}. ${item.title}`}
                      description={`Duration: ${Math.floor(item.duration / 60)}:${(item.duration % 60)
                        .toString()
                        .padStart(2, '0')}s`}
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Drawer>

      {/* Add Video Modal */}
      <Modal
        title="Add Video to Playlist"
        open={addVideoModalOpen}
        onOk={() => addVideoForm.submit()}
        onCancel={() => {
          setAddVideoModalOpen(false);
          addVideoForm.resetFields();
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form
          form={addVideoForm}
          layout="vertical"
          onFinish={handleAddVideo}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="video_id"
            label="Select Video"
            rules={[{ required: true, message: 'Please select a video' }]}
          >
            <Select
              placeholder="Choose a video..."
              options={videos.map((v) => ({
                label: v.title,
                value: v.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
